/* eslint-disable camelcase */
import {
  createSelector,
} from 'reselect';

import type {
  Id,
  Resources,
  ResourceType,
  ClientResourceType,
} from '../types';

import {
  getResourceMap,
  getResourceById,
  getResourceMappedList,
  getSelectionResourceMappedList,
} from './resources';

import {
  alphSortByName,
} from './utils';

export const getEntitySearchOptions = (resourceType, list = 'all') => createSelector(
  getResourceMappedList(resourceType, list),
  (all) => {
    switch (resourceType) {
      case 'personalWorkouts':
        return all
          .filter(w => !w.is_for_single_exercise && !w.is_practice)
          .sort(alphSortByName)
          .map(
            ({ id, name, ...rest }) => ({
              label: name,
              value: String(id),
              rest,
            }),
          );
      default:
        return all
          .sort(alphSortByName)
          .map(
            ({ id, name, ...rest }) => ({
              label: name,
              value: String(id),
              rest,
            }),
          );
    }
  },
);

const selectedBreadcrumbs = {};
export const getBreadcrumbs = (resourceType, id) => {
  if (selectedBreadcrumbs[`${resourceType}_${id}`]) {
    return selectedBreadcrumbs[`${resourceType}_${id}`];
  }
  selectedBreadcrumbs[`${resourceType}_${id}`] = createSelector(
    [
      getResourceMappedList('folders'),
      getResourceById(resourceType, id),
    ],
    (
      allFolders,
      resource,
    ) => {
      if (resource
        && resource.folder_id === undefined
        && resource.parent_id === undefined) {
        return [resource];
      }

      const tree = items => items.reduce((acc, item) => [].concat(
        tree(
          allFolders.filter(f => f.id === (item.folder_id || item.parent_id)),
        ),
        ((item.folder_id || item.parent_id) ? item : []), acc,
      ), []);

      return tree(
        resource ? [resource] : allFolders.filter(f => f.id === id),
      );
    },
  );
  return selectedBreadcrumbs[`${resourceType}_${id}`];
};

export const getFolderItems = (resourceType: ResourceType, folderId: number) => createSelector(
  getSelectionResourceMappedList(resourceType, 'library'),
  items => items.filter(item => item.folder_id === folderId),
);

const sortByName = (items: any[], filter: any) => items.filter(filter).sort(
  (a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), undefined, {
    numeric: true,
    sensitivity: 'base',
  }),
);

export const getChildrenFolderById = (resourceType: ResourceType, id: number) => createSelector(
  [
    getSelectionResourceMappedList('folders'),
    getSelectionResourceMappedList(resourceType, 'library'),
    getResourceById('folders', id),
  ],
  (
    allFolders,
    resources,
    folder,
  ) => {
    const items: any[] = [];

    // Add special folders for Workout Templates
    if (resourceType === 'workoutTemplates' && folder?.name === 'Workout Templates') {
      items.push(
        ...allFolders.filter(f => f.is_warmap || f.is_cooldown)
      );
    }

    // Filter resources based on type
    const filteredResources = resourceType === 'workoutTemplates'
      ? resources.filter(r => r.is_visible)
      : resources;

    // Combine and return sorted items
    return [
      ...items,
      ...allFolders.filter(f => f.parent_id === id).sort(alphSortByName),
      ...filteredResources.filter(r => r.folder_id === id).sort(alphSortByName),
    ];
  },
);

type FolderKeys = 'Documents' | 'Exercises' | 'Workout Templates' | 'Warmup Templates' | 'Cooldown Templates' | 'Program Templates';
let selectedFolderRootsSelector: (state: Resources) => { [key: string]: Id };

export const getFolderRoots = (keyOrState: string | Resources) => {
  if (!selectedFolderRootsSelector) {
    selectedFolderRootsSelector = createSelector(
      getResourceMappedList('folders', 'roots'),
      (folders) => {
        const keys: { [key in FolderKeys]: string } = {
          Documents: 'documentId',
          Exercises: 'exerciseId',
          'Workout Templates': 'workoutId',
          'Warmup Templates': 'warmupId',
          'Cooldown Templates': 'cooldownId',
          'Program Templates': 'programId',
        };
        return folders.reduce((acc, folder) => ({
          ...acc,
          [keys[folder.name as FolderKeys]]: folder.id,
        }), {});
      },
    );
  }

  if (typeof keyOrState === 'string') {
    return (state: Resources) => selectedFolderRootsSelector(state)[keyOrState];
  }

  return selectedFolderRootsSelector(keyOrState);
};

const foldersSelectors = {};
export const getTreeFolders = (folderName) => {
  if (foldersSelectors[folderName]) {
    return foldersSelectors[folderName];
  }
  foldersSelectors[folderName] = createSelector(
    [
      getSelectionResourceMappedList('folders'),
    ],
    (
      folders,
    ) => {
      const tree = items => items.reduce((acc, folder) => [
        ...acc,
        {
          ...folder,
          children: [].concat(
            tree(
              folders
                .filter(f => f.parent_id === folder.id)
                .sort(alphSortByName),
            ),
          ),
        },
      ], []);
      return tree(
        folders.filter(
          folder => folder.is_root && folder.name === folderName,
        ),
      )[0];
    },
  );
  return foldersSelectors[folderName];
};

const rootNames = {
  exercises: 'Exercises',
  documents: 'Documents',
  warmups: 'Warmup Templates',
  cooldowns: 'Cooldown Templates',
  workoutTemplates: 'Workout Templates',
  programTemplates: 'Program Templates',
};

export const getEntityFolders = (resourceType: ResourceType) => createSelector(
  [
    getResourceMappedList('folders', 'roots'),
    getResourceMappedList('folders'),
  ],
  (roots, folders) => {
    const root = roots.find(r => r.name === rootNames[resourceType]);
    if (root) {
      const items = folders
        .filter(f => f.parent_id === root.id)
        .sort(alphSortByName);

      return [
        root,
        ...items,
      ];
    }
    return [];
  },
);

export const getChildFolders = folderId => createSelector(
  getSelectionResourceMappedList('folders'),
  folders => folders
    .filter(f => !f.is_root && !f.selected && f.parent_id === folderId)
    .sort(alphSortByName),
);

export const getTagOptions = createSelector(
  getSelectionResourceMappedList('tags'),
  tags => tags
    .sort(alphSortByName)
    .map(
      tag => ({
        value: tag.id,
        label: tag.name,
      }),
    ),
);

export const getTagedItems = (tag, tagType) => createSelector(
  [
    getResourceMappedList('exercises'),
    getResourceMappedList('workoutTemplates'),
    getResourceMappedList('warmups'),
    getResourceMappedList('cooldowns'),
    getResourceMappedList('programTemplates'),
    getResourceMappedList('documents'),
  ],
  (
    exercises,
    workoutTemplates,
    warmups,
    cooldowns,
    programTemplates,
    documents,
  ) => {
    const mapped = (items, type) => items
      .filter(item => item.tag_list.includes(tag))
      .map(
        item => ({
          ...item,
          type,
        }),
      );

    switch (tagType) {
      case 'exercises':
        return mapped(exercises, 'exercises');
      case 'workouts':
        return [
          ...mapped(workoutTemplates, 'workout_templates'),
          ...mapped(warmups, 'warmups'),
          ...mapped(cooldowns, 'cooldowns'),
        ];
      case 'programs':
        return mapped(programTemplates, 'program_templates');
      case 'documents':
        return mapped(documents, 'documents');
      default:
        return [
          ...mapped(exercises, 'exercises'),
          ...[
            ...mapped(workoutTemplates, 'workout_templates'),
            ...mapped(warmups, 'warmups'),
            ...mapped(cooldowns, 'cooldowns'),
          ],
          ...mapped(programTemplates, 'program_templates'),
          ...mapped(documents, 'documents'),
        ];
    }
  },
);

export const getSearchItems = (type, searchQuery) => createSelector(
  [
    getResourceMappedList('exercises'),
    getResourceMappedList('workoutTemplates'),
    getResourceMappedList('warmups'),
    getResourceMappedList('cooldowns'),
    getResourceMappedList('programTemplates'),
    getResourceMappedList('documents'),
    getResourceMappedList('clients'),
    getResourceMap('clients'),
  ],
  (
    exercises,
    workoutTemplates,
    warmups,
    cooldowns,
    programTemplates,
    documents,
    clients,
    clientsMap,
  ) => {
    const mapped = (items, entityType) => items
      .filter(item => {
        if (!searchQuery) return true;
        return item.name
          .toLowerCase()
          .indexOf(searchQuery.toLowerCase()) !== -1;
      })
      .map(
        item => {
          const profile = clientsMap[item.author_id]?.user_profile;

          return {
            ...item,
            ...(profile
              ? {
                author_full_name: `${profile.first_name} ${profile.last_name}`,
              }
              : {}
            ),
            entityType,
          };
        },
      )
      .sort(alphSortByName);

    const mappedClients = clients
      .filter(item => !item.is_pro)
      .map(item => ({
        ...item,
        name: `${item.user_profile?.first_name || ''} ${item.user_profile?.last_name || ''}`.trim(),
      }))
      .filter(item => {
        if (!searchQuery) return true;
        return item.name
          .toLowerCase()
          .indexOf(searchQuery.toLowerCase()) !== -1;
      })
      .map(item => ({
        ...item,
        entityType: 'clients',
      }))
      .sort(alphSortByName);

    switch (type) {
      case 'clients':
        return mappedClients;
      case 'exercises':
        return mapped(exercises, 'exercises');
      case 'workouts':
        return [
          ...mapped(workoutTemplates, 'workouts'),
          ...mapped(warmups, 'workouts'),
          ...mapped(cooldowns, 'workouts'),
        ];
      case 'programs':
        return mapped(programTemplates, 'programs');
      case 'documents':
        return mapped(documents, 'documents');
      default:
        return [
          ...mappedClients,
          ...mapped(exercises, 'exercises'),
          ...[
            ...mapped(workoutTemplates, 'workouts'),
            ...mapped(warmups, 'workouts'),
            ...mapped(cooldowns, 'workouts'),
          ],
          ...mapped(programTemplates, 'programs'),
          ...mapped(documents, 'documents'),
        ];
    }
  },
);

const foldersWithItemsSelectors = {};
export const getTreeFoldersWithItems = (resourceType) => {
  if (foldersWithItemsSelectors[resourceType]) {
    return foldersWithItemsSelectors[resourceType];
  }

  foldersWithItemsSelectors[resourceType] = createSelector(
    [
      getSelectionResourceMappedList('folders'),
      getSelectionResourceMappedList(resourceType, 'library'),
    ],
    (
      allFolders,
      allItems,
    ) => {
      const tree = (folders) => folders.reduce((acc, item) => [
        ...acc,
        {
          id: item.id,
          name: item.name,
          selected: item.selected,
          isFolder: true,
          children: [].concat(
            tree(
              allFolders
                .filter(folder => folder.parent_id === item.id)
                .sort(alphSortByName),
            ),
            allItems
              .filter(folder => folder.folder_id === item.id)
              .sort(alphSortByName),

          ),
        },
      ], []);

      return tree(
        allFolders.filter(folder => folder.name === rootNames[resourceType]),
      )[0];
    },
  );

  return foldersWithItemsSelectors[resourceType];
};

export const getFolderItemsSortedByName = (resourceType: ClientResourceType) => createSelector(
  getResourceMappedList(resourceType, 'library'),
  (items) => {
    switch (resourceType) {
      case 'personalWorkouts':
        return items
          .filter(w => !w.is_for_single_exercise && !w.is_practice)
          .sort(alphSortByName);
      case 'personalExercises':
        return items
          .filter(e => !e.is_practice)
          .sort(alphSortByName);
      default:
        return items.sort(alphSortByName);
    }
  },
);

export const getExerciseFolder = createSelector(
  (state: Resources) => {
    const folderId = getFolderRoots('exerciseId')(state);
    if (!folderId) return [];

    const items = getChildrenFolderById(
      'exercises',
      folderId,
    )(state);

    return items.find(i => i.parent_id);
  },
  (workout) => workout,
);

export const getFirstWorkout = createSelector(
  (state: Resources) => {
    const folderId = getFolderRoots('workoutId')(state);
    if (!folderId) return [];

    const items = getChildrenFolderById(
      'workoutTemplates',
      folderId,
    )(state);

    return items.find(i => !i.parent_id);
  },
  (workout) => workout,
);
